import { dmEnv } from '@dm/utils';
import { StaticToast } from 'src/components/common';
import { checkRights } from 'src/pages/auth/api';
import { appletVersion, getEnv, webviewURL } from 'src/utils/env';
import format from 'src/utils/format';
import { Serialize, Deserialize } from 'src/utils/serialization';
import { InquiryStatus, THIRD_PARTNER_AUTH_CONFIG, THIRD_PLATFORM_RESOURCECODE, THIRD_RESOURCE_APPLET_CODE, hospitalAlipayAppId, ownAppid, zaFsHealthAlipayAppId, zajkAppid } from 'src/utils/staticData';
import validate from 'src/utils/validate';
import bridge, { getAliOpenInfo } from './bridge';
import { getChannelResourceCode, getIsBlackUser } from './channel';
import cookies from './cookie';
import { fetchJson } from './fetch';
import { xflowPushEvent } from './pageTrack';
import storage from './storage';
import { getAppSystemInfo } from './tim/open';
import { getInquiryOrMentalConsultListUrl } from './tool';

/**
 * 为URL添加wxobsMetadata参数
 */
export function addWxobsMetadataToUrl(urlString: string): string {
  const url = new URL(urlString);
  // 只对 *.za-doctor.com 的域名添加 wxobsMetadata 参数
  if (url.hostname.includes('za-doctor.com')) {
    // 防止 getAuxiliaryDomainInfo 方法执行时报错
    try {
      const auxiliaryDomainInfo = window.__wxobs__?.getAuxiliaryDomainInfo() || '';
      if (auxiliaryDomainInfo) {
        url.searchParams.set('wxobsMetadata', auxiliaryDomainInfo);
      }
    } catch (error) {
      xflowPushEvent(['click', 'H5_WXOBS_Error', 'H5获取wxobsMetadata异常', { extra_infomation: { error } }]);
      console.error('Error getting auxiliary domain info:', error);
    }
  }
  return url.toString();
}

/**
 * partnerCode === 'SYY' 时 cardType 和 cardNumber 必传
 */
interface JumpToApplet {
  appId: string;
  partnerServerUrl: string;
  token: string;
  [k: string]: any;
}

export function jumpToApplet({ appId, partnerServerUrl, token }: JumpToApplet) {
  if (validate.isFromMiniApplet()) {
    let path = partnerServerUrl;

    if (token) {
      path += token;
    }

    wx.miniProgram.redirectTo({
      url: `/pages/middleMiniapp?appId=${appId}&needAutoJump=${appId === zajkAppid ? '0' : '1'}&envVersion=${appletVersion}&path=${encodeURIComponent(path)}`,
    });
  } else {
    StaticToast.error('本平台暂不支持使用云药房，请至微信-众安互联网医院小程序 使用。');
  }
}

interface GjRightState {
  auth: boolean;
  url: string;
}
// 查看是否拥有高技药房的权限
export const checkUserIsGJRight = () => new Promise((resolve: (value: GjRightState) => void) => {
  fetchJson({
    url: '/api/api/v1/patient/gjmall/indexUrl',
    type: 'POST',
    needLogin: true,
    needLoading: true,
  }).then((res) => {
    const { code, result = {} } = res || {};
    const { url = '' } = result || {};

    if (code === '0') {
      resolve({
        auth: !!url,
        url,
      });
    } else {
      resolve({
        auth: false,
        url: '',
      });
    }
  });
});

interface UserIsThirdAuthOption {
  partnerCode?: string;
  businessType?: string;
  thirdPlatformCode?: string;
  bizNo?: string | number;
  policyNo?: string;
  thirdBizNo?: string;
  isReplacePage?: boolean;
  thirdBizType?: string;
  thirdOrderId?: string;
  bizId?: string | number;
  channelPoint?: string;
  target?: string;
  redirectUrl?: string;
  relatedBizNo?: string;
  backUrl?: string;
  city?: string;
  thirdUserId?: string;
  patientId?: string;
  actualOpenId?: string;
  appCode?: string;
  promoteCode?: string;
  decode?: string;
  isCheckRights?: string;
  freeTrial?: 'Y' | 'N';
  isApplet?: string
  userRightsId?: string,
  inquiryId?: string,
  cardNo?: string,
  orderNo?: string,
  policyPersonDomain?: {
    certNo: string,
    name: string,
  },
  mentalReleaseQueryDto?: Object,
  prescriptionId?: string | number,
}
const redirectBeforeAuthUrl = (url, isReplacePage) => isReplacePage ? location.replace(url) : (location.href = url);
// 检测当前用户是否有进行鉴权过
// export const validateUserIsThirdAuth
export const jumpBeforeAuth = async (option: UserIsThirdAuthOption = {}) => {
  console.log('UserIsThirdAuthOption', option);
  const {
    partnerCode = '', businessType = '', thirdPlatformCode = '', bizNo = '',
    policyNo = '', thirdBizNo = '', thirdBizType = '', thirdOrderId,
    isReplacePage = false, bizId = '', channelPoint = '', target = '',
    relatedBizNo = '', backUrl = '', city, redirectUrl = '',
    thirdUserId = '', patientId = '', decode = '', isCheckRights = '', freeTrial = 'N',
    userRightsId,
    inquiryId,
    cardNo,
    orderNo,
    policyPersonDomain,
    mentalReleaseQueryDto,
    prescriptionId,
  } = option;
  const _thirdPlatformCode = thirdPlatformCode || partnerCode;
  if (!_thirdPlatformCode && (!businessType && !target)) {
    StaticToast.warning('缺少跳转的必要参数');
    return;
  }
  cookies.set('jumpToThird', 'Y');

  if (validate.isFromMiniApplet() && !validate.isFromOwnMiniApplet()) {
    const thirdResourceApplet = storage.get('thirdResourceApplet');
    // 众安保内嵌h5心理测评的入口
    const zabAuth = businessType == THIRD_PARTNER_AUTH_CONFIG.YDL_HEARTASSESS.businessType && thirdResourceApplet === THIRD_RESOURCE_APPLET_CODE.ZABXCX;

    // 众安健康内嵌h5自有够药商城入口
    const zajkAuth = validate.isFromZajkMiniApplet() && _thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.ZA_MALL;

    // 众安保险小程序内嵌互医H5方伞
    const zabxFs = validate.isFromZabxMiniApplet() && _thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.ZA_UMBRELLA;

    // 允许众安健康商城（原互医商城）
    const zaHyMall = validate.isFromHyMallMiniApplet() && _thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.HY_MALL;

    if (!zabAuth && !zajkAuth && !zabxFs && !zaHyMall) {
      StaticToast.error('本平台暂不支持使用，请至微信-众安互联网医院小程序 使用。');
      return;
    }
  }
  const params: UserIsThirdAuthOption =  {
    thirdPlatformCode: thirdPlatformCode || partnerCode,
    businessType,
    bizNo: bizNo || policyNo, // policyNo 为罗诊保险产品对接需要传入
    thirdBizNo,
    thirdBizType,
    thirdOrderId,
    bizId,
    channelPoint,
    target: decode ? decodeURIComponent(target) : target,
    relatedBizNo,
    backUrl,
    city,
    policyNo,
    redirectUrl: decodeURIComponent(redirectUrl),
    thirdUserId, // 支付宝小程序ID，上药云云药箱需要使用
    patientId,
    userRightsId,
    inquiryId,
    cardNo,
    orderNo,
    mentalReleaseQueryDto,
    prescriptionId,
  };

  if(validate.isFromOwnMiniApplet()) {
    params.actualOpenId = cookies.get('openId') || '';
    params.appCode = 'hy';
  }

  // 前往方伞,如果是从保险小程序跳转，需要带上appCode和openId
  if(_thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.ZA_UMBRELLA && validate.isFromZabxMiniApplet()){
    params.actualOpenId = cookies.get('openId') || '';
    params.appCode = 'ZABXMiniApp';
  }

  // 在互医小程序中，前往益丰，授权时需要带上appId和openId。
  if (_thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.YIFENG && validate.isFromOwnMiniApplet()) {
    params.actualOpenId = cookies.get('openId') || '';
    params.appCode = storage.get('ownAppletAppid');
  }

  // 在互医小程序中，前往憧泰，授权时需要带上penId。
  if (_thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.CHONGTAI && validate.isFromOwnMiniApplet()) {
    params.actualOpenId = cookies.get('openId') || '';
  }

  if (_thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.YIYH && validate.isFromOwnMiniApplet()) {
    params.actualOpenId = cookies.get('openId') || '';
    params.isApplet = 'Y';
  }

  if (_thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.SANGGUO && validate.isFromOwnMiniApplet()) {
    params.actualOpenId = cookies.get('openId') || '';
    params.isApplet = 'Y';
  }

  // 如果是益丰，且url上有校验商城权益的参数，则需要调用接口获取用户是否有商城权益
  if (partnerCode === THIRD_PLATFORM_RESOURCECODE.YIFENG && isCheckRights === 'Y') {
    const rightCheckRes = await checkRights();
    const { code, result } = rightCheckRes || {};
    if (code === '0' && !result) {
      // 益丰体验版
      if (freeTrial === 'Y') {
        const originTarget = decodeURIComponent(params.target!);
        const [url, qs] = originTarget.split('?');
        const queryParams = qs ? Deserialize(qs) : {};
        if (!queryParams.userLabel || queryParams.userLabel !== '1') {
          queryParams.userLabel = '1';
          const qsModified = Serialize(queryParams);
          params.target = encodeURIComponent(`${url}?${qsModified}`);
        }
      } else {
        location.replace('/hospital/static/no-rights');
        return;
      }
    }
  }

  if (policyPersonDomain && Object.keys(policyPersonDomain).length) {
    params.policyPersonDomain = policyPersonDomain;
  }

  // 当h5处于支付宝小程序时，需要将宿主环境的appId和openId一并给到授权接口（益丰），让后端交给第三方
  if (partnerCode === THIRD_PLATFORM_RESOURCECODE.YIFENG && validate.isAlipayApplet()) {
    const { aliAppletOpenId, aliAppletPartnerCode } = await getAliOpenInfo();
    switch (aliAppletPartnerCode) {
    case 'ZA_ASCLEPIUS_ALIPAY':
      params.appCode = hospitalAlipayAppId;
      break;
    case 'ZA_FSJK':
      params.appCode = zaFsHealthAlipayAppId;
      break;
    default:
      break;
    }
    params.actualOpenId = aliAppletOpenId;
  }

  const needRealAuthList = [THIRD_PLATFORM_RESOURCECODE.WEIYI_SERVICE]; // 需要实名证件信息的第三方：微医挂号
  fetchJson({
    url: '/api/api/v1/patient/partner/user/auth/isUserAuth',
    data: params,
    type: 'POST',
    needLoading: true,
  })
    .then((res) => {
      if (res.code == 0) {
        const { appId, isUserAuth = '', jumpType, partnerServerUrl = '', token = '' } = res.result || {};
        const { pathname = '' } = window.location;
        let completeUrl = `${/^https?:\/\/.+/.test(token) ? '' : partnerServerUrl}${token}`;
        if (isUserAuth === 'N') {
          const replacePath = `/hospital/${needRealAuthList.includes(_thirdPlatformCode) ? '' : 'noid'}auth`;
          window.reactHistory[isReplacePage ? 'replace' : 'push']({
            pathname: replacePath,
            search: Serialize({
              ...option,
              partnerCode: _thirdPlatformCode,
              policyPersonDomain: JSON.stringify(policyPersonDomain || {}),
            }),
          });
        } else if (jumpType === 'applet') {
          jumpToApplet({
            appId,
            partnerServerUrl,
            token,
          });
        } else {
          if(thirdPlatformCode === 'WEI_FAN') {
            jumpToWeifan(token, thirdBizType);
            return;
          }
          // 针对直营APP，在跳转第三方前，把页面标题设置成“众安互联网医院”
          dmEnv.isApp() && bridge.setTitle('众安互联网医院');

          if (_thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.ZA_MALL || partnerCode === THIRD_PLATFORM_RESOURCECODE.ZA_UMBRELLA) {
            // 众安健康小程序: ZA_HEALTH  众安互联网医院: ZA_HOSPITAL  众安保险app: ZA_INSURANCE_APP
            const connector = completeUrl.includes('?') ? '&' : '?';
            if (format.GetQueryString('channelResourceCode') === 'JKXSSY2021YYLB') {
              completeUrl = `${completeUrl}${connector}source=ZA_HEALTH`;
            } else if (dmEnv.isApp()) {
              completeUrl = `${completeUrl}${connector}source=ZA_INSURANCE_APP`;
            } else {
              completeUrl = `${completeUrl}${connector}source=ZA_HOSPITAL&appCode=hy&openId=${cookies.get('openId') || ''}`;
            }
          }
          if (pathname === '/hospital/static/yunyaofang' || thirdBizType === 'detail') {
            if (thirdBizType === 'detail' && thirdBizNo) {
              const qs = token.replace(/(\/#\/)\?/, (match, p1) => `${p1}pages/wode/goodsDetail?id=${thirdBizNo}&`);
              completeUrl = `${partnerServerUrl}${qs}`;
            }
            redirectBeforeAuthUrl(completeUrl, isReplacePage);
            // location.href = completeUrl;
          } else if (pathname === '/hospital/login' || isReplacePage) {
            location.replace(addWxobsMetadataToUrl(completeUrl));
          } else {
            // 小鹿直接返回
            if (_thirdPlatformCode === THIRD_PLATFORM_RESOURCECODE.XLZY) {
              location.replace(token); // 直接包涵了地址
            } else {
              const url = addWxobsMetadataToUrl(completeUrl);
              // location.href = completeUrl;
              location.href = url;
            }
          }
        }
        return;
      }

      xflowPushEvent(['click', 'H5_Auth_Fetch_Fail', 'H5鉴权获取token失败', { extra_infomation: { error: res } }]);
    })
    .catch((err) => {
      console.log(err);
      StaticToast.warning('请求鉴权出错');
      xflowPushEvent(['click', 'H5_Auth_Error', 'H5鉴权异常', { extra_infomation: { error: err } }]);
    });
};

// 针对引导用户去加微信页的引导
export const jumpToWechatGuidance = (url = 'https://mp.weixin.qq.com/s/N_rI9KNfvWkwLIjC0GacYw') => {
  // let { staffNo = '', doctorType = 0 } = options || {};
  // const userServiceType = doctorType == 1 ? '家庭医生' : '健康管家';
  // window._XFLOW_.pushEvent(['click', 'ZAHLWYY_QYWX', '企业微信导流', { ZAHLWYY_CLICK_CONTENT: `添加企业微信_${userServiceType}_${word}` }]);
  xflowPushEvent(['click', 'ZAHLWYY_QYWX', '企业微信导流', { ZAHLWYY_CLICK_CONTENT: '添加企业微信' }]);
  const isBlackUser = getIsBlackUser();
  if (isBlackUser) {
    const errorUrl = '?src=' + encodeURIComponent(`${webviewURL}/hospital/error`) + `&param=${JSON.stringify({ type: 'black' })}`;
    if (validate.isAlipayApplet()) {
      my.redirectTo({
        url: `/pages/webview/index${errorUrl}`,
      });
    } else if (validate.isFromOwnMiniApplet()) {
      // 小程序环境内，跳转承接方的中间页
      wx.miniProgram.redirectTo({
        url: `/pages/webview${errorUrl}`,
      });
    } else {
      window.location.href = '/hospital/error?type=black';
    }
    return;
  }
  window.location.href = url;
};

const textInquiryToAwait = (needTextRedirectTo = false, query = {} as any, needReplace = false) => {
  const { dictionary: { SYSTEMMODE = [] } = {} } = window.globalStore.getState();
  const supervisionModel = (SYSTEMMODE[0] || {}).resCode;
  const isInquiryform = supervisionModel === 'supervision' ? false : window.location.pathname.includes('/hospital/inquiryform');
  const { inquiryStatus, inquiryType } = query;
  /** 根据问诊单状态跳转不同页面  */
  let url = isInquiryform ? '/hospital/inquiryform' : '/hospital/await';

  // 以下状态都需要直接跳转到聊天页面（单指图文）
  if (inquiryType === 'I' && [InquiryStatus.INQUIRY_ING, InquiryStatus.INQUIRY_END, InquiryStatus.INQUIRY_EVALUATED].includes(Number(inquiryStatus))) {
    url = '/hospital/chat';
  }

  if(isInquiryform) {
    query = {
      ...query,
      awaitPage: '1',
    };
  }
  const appletUrl = encodeURIComponent(`${webviewURL}${url}`) + `&param=${JSON.stringify(query)}`;
  if (validate.isAlipayApplet() && needTextRedirectTo) {
    my.redirectTo({
      url: `/pages/webview/index?src=${appletUrl}`,
    });
  } else if (validate.isFromOwnMiniApplet() && needTextRedirectTo) {
    // 本地调试涉及webviewURL的时候记得去utils/env文件下面修改DEPLOY__ENV的默认环境，不然会默认往生产跳转！！！！！
    wx.miniProgram.redirectTo({
      url: `/pages/webview?src=${appletUrl}`,
    });
  } else {
    needReplace ? window.reactHistory.replace({
      pathname: url,
      search: Serialize(query),
    }) : window.reactHistory.push({
      pathname: url,
      search: Serialize(query),
    });
  }
};

const envNotSupportInquriy = (position = '', inquiryTimingType = '') => {
  // 如果已在问诊 or 咨询列表页，就不用进入该页面
  if (!/\/hospital\/my(mentalConsult|inquiry)/i.test(window.location.pathname) && position !== 'checkInquiry') {
    window.location.replace(getInquiryOrMentalConsultListUrl(inquiryTimingType));
    return;
  }
  StaticToast.warning('本平台暂不支持视频问诊，请前往众安互联网医院小程序体验');
};

/* 问诊前先查询医生问诊能力*/
export const jumpBeforeFetchThirdInquiryAbility = async (options) => {
  const { data: { inquiryNo = '', inquiryId = '', id: _inquiryId = '' } = {}, needTextRedirectTo = false, needInquiryDeatil = true, needReplace } = options;
  let inquiryDetailData = options.data || {};
  let query = { inquiryId: inquiryId || _inquiryId, inquiryNo, inquiryStatus: '', inquiryType: '' };
  if (!inquiryNo && !(inquiryId || _inquiryId)) {
    StaticToast.error('缺少问诊单号');
    return false;
  }
  if (needInquiryDeatil) {
    const res = await fetchJson({
      type: 'POST',
      url: '/api/api/v1/patient/inquiry/detail',
      isloading: true,
      data: {
        ...query,
        option: {
          needThirdRelation: true,
        },
      },
    });
    const { result = {} } = res || {};
    inquiryDetailData = result;
    if (res && res.code !== '0') {
      StaticToast.error('查询问诊单出错');
      return;
    }
  }
  const { platformCode = '', inquiryType = '', inquiryTimingType = '', inquiryStatus = '' } = inquiryDetailData;
  // 更新问诊单状态
  query = {
    ...query,
    inquiryStatus,
    inquiryType,
  };
  // 图文问诊
  if (inquiryType === 'I') {
    // window.location.href = `?${}`
    textInquiryToAwait(needTextRedirectTo, query, needReplace);
  } else {
    const appVerIsCanOnlineVideo = await getAppSystemInfo();
    const supportVideoInquriry = (dmEnv.isApp() && appVerIsCanOnlineVideo) || validate.isBestpay();
    if ((supportVideoInquriry && THIRD_PLATFORM_RESOURCECODE.ZAHY !== platformCode) || validate.isFromMiniApplet() || validate.isAlipayApplet()) {
      if (validate.isFromMiniApplet() && inquiryTimingType === 'appointment' && THIRD_PLATFORM_RESOURCECODE.ZAHY === platformCode) {
        const nextSearch = Serialize(query);
        const path = `/pages/video/selfChat?${nextSearch}&inquiryStatus=${inquiryStatus}`;
        jumpToAppletPublicMethod(path);
        return;
      }
      textInquiryToAwait(needTextRedirectTo, query);
    } else {
      envNotSupportInquriy('', inquiryTimingType);
    }
  }
  return;
};

export const jumpToZajkApplet = (path) => {
  // 众安健康小程序跳转 /pages/redirectTo/index?data='{appId: '', path: ''}'
  const zajkData = {
    appId: ownAppid,
    path: `/pages/autoLogin?target=${encodeURIComponent(path)}`,
  };
  wx.miniProgram.redirectTo({
    url: `/pages/redirectTo/index?data=${encodeURIComponent(JSON.stringify(zajkData))}`,
  });
};

export const jumpToZabxMinddlePage = (path, orderNo = '') => {
  // 小程序页面路径：/pages/main/middle-miniapp/index
  // 跳转成功或失败，默认都回退上一页。如果有带successExtUrl参数，跳转成功时额外跳转链接，用于返回当前小程序时展示其它页面
  const searchs = `thirdResourceApplet=${storage.get('thirdResourceApplet')}`;
  const target = `${path}${path.includes('?') ? '&' : '?'}${searchs}`;
  const [url = ''] = path.split('?');
  let successExtUrl = `${location.pathname}${location.search}`;
  const isPay = ['/pages/pay', '/pages/commonThirdPay'].includes(url);
  if (isPay) {
    successExtUrl = `/hospital/third/pay-carrier-result?orderNo=${orderNo}`;
    // successExtUrl = `&successExtUrl=${encodeURIComponent(successExtUrl)}`;
  }
  successExtUrl = `${location.origin}/hospital/init?target=${encodeURIComponent(successExtUrl)}&channelResourceCode=${getChannelResourceCode()}&token=${cookies.get('za_token') || ''}&${searchs}`;
  path = `/pages/autoLogin?target=${encodeURIComponent(target)}&channelResourceCode=${getChannelResourceCode()}&token=${cookies.get('za_token') || ''}`;
  console.log('------------path-------------', path);
  wx.miniProgram.redirectTo({
    url: `/pages/main/middle-miniapp/index?appId=${ownAppid}&btnText=去互联网医院小程序${isPay ? '完成支付' : ''}&path=${encodeURIComponent(path)}&successExtUrl=${encodeURIComponent(successExtUrl)}&envVersion=trial`,
  });
};

export const jumpToAppletPublicMethod = (path, orderNo = '', canBack = false) => {
  if (validate.isFromOwnMiniApplet()) {
    const [url = ''] = path.split('?');
    if (['/pages/healthManage', '/pages/personalcenter', '/pages/index'].includes(url)) {
      wx.miniProgram.switchTab({
        url: path,
      });
      return;
    }
    if (canBack) {
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      wx.miniProgram.redirectTo({
        url: path,
      });
    }
  } else if (validate.isFromZajkMiniApplet()) {
    jumpToZajkApplet(path);
  } else {
    jumpToZabxMinddlePage(path, orderNo);
  }
};

/**
 * @description 跳转支付宝小程序公共方法
 */
export const jumpToAliAppletPublicMethod = (path) => {
  if (validate.isAlipayApplet()) {
    my.redirectTo({
      url: path,
    });
  }
};

const rightsUrl = '/pages/web/index?i=1v6zz5c';
const encodeRightsUrl = encodeURIComponent(rightsUrl);

export const openRights = () => {
  if (validate.isFromMiniApplet()) {
    if (validate.isFromOwnMiniApplet()) {
      wx.miniProgram.redirectTo({
        url: `/pages/middleMiniapp?appId=wxbac45cc1588a5a75&path=${encodeRightsUrl}&envVersion=trial`,
      });
      return;
    }

    if (validate.isFromZajkMiniApplet()) {
      wx.miniProgram.navigateTo({
        url: rightsUrl,
      });
      return;
    }

    wx.miniProgram.navigateTo({
      url: `/pages/main/middle-miniapp/index?appId=${zajkAppid}&path=${encodeRightsUrl}&envVersion=trial`,
    });
  } else {
    location.href = 'https://m.zhongan.com/gp/91228109?cid=10002542175&templateType=B&bizOrigin=ZAmedical_UserRight_GetRight';
  }
};

export const generateTokenForClinic = (props) => {
  fetchJson({
    url: '/api/api/v1/patient/partner/user/auth/generateTokenForOrderId',
    type: 'GET',
    data: {
      orderId: props.orderId,
    },
    isloading: true,
  }).then((res) => {
    if (res && res.code === '0') {
      const { result } = res || {};
      console.log('token', result.token);
      location.replace(result.token);
    }
  });
};

/**
 * 跳转到唯凡健康小程序
 *
 * @param token 用户令牌
 */
/**
 * 根据业务类型获取唯凡类型
 *
 * @param thirdBizType 第三方业务类型
 * @returns 唯凡类型: offline(线下康复)、online(线上康复)、zmb(中医保)
 */
function getWeifanType(thirdBizType: string): string {
  // 1. 臻享26
  if (thirdBizType === 'zhenxiangservice26_kfll_ys') {
    return 'zmb';
  }

  // 2. 线下康复理疗
  if (thirdBizType === 'ZXHealthService_kfll_yx' || thirdBizType === 'ZXHealthServiceplus_kfll_yx') {
    return 'offline';
  }

  if (thirdBizType === 'PremiumHealthplus26_kfll_ys') {
    return 'consultation';
  }

  // 3. 默认线上服务
  return 'online';
}

export function jumpToWeifan(token: string, thirdBizType): void {
  const appId = 'wx0323cf9b9a2e2a1a';
  const path = 'pages/campaign/campaign';
  const type = getWeifanType(thirdBizType);

  // 判断是否在微信小程序内嵌H5环境
  if (validate.isFromOwnMiniApplet()) {
    // 微信小程序内嵌H5环境，需要使用中转页面
    const appletVersion = getEnv() === 'prd' ? 'release' : 'trial';

    // 构建带有token的完整路径
    const fullPath = `${path}?token=${token}&type=${type}`;

    wx.miniProgram.navigateTo({
      url: `/pages/middleMiniapp?appId=${appId}&needAutoJump=1&envVersion=${appletVersion}&path=${encodeURIComponent(path)}&extraData=${JSON.stringify({
        token,
        type,
      })}`,
    });
  } else {
    // 非微信小程序环境，根据当前环境选择跳转链接
    const isProduction = getEnv() === 'prd';
    const redirectUrl = isProduction
      ? `https://wf-api.weifanhealth.cn/web/redirect/miniprogram?token=${token}&type=${type}`
      : `https://codex.sh.cn/backend-web/redirect/miniprogram?token=${token}&type=${type}`;

    window.location.href = redirectUrl;
  }
}
